import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  useTheme,
} from "@mui/material";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import Toolbar from "./Toolbar";
import DPad from "./DPad";
import Locator from "./Locator";

// ----------------------------------------------------------------
// Tile Groups with explicit number assignments (0 is reserved for "nothing")
// ----------------------------------------------------------------
const tileGroups: {
  L0: { [key: string]: number };
  L1: { [key: string]: number };
} = {
  L0: {
    grass: 1,
    forest: 2,
    mountain: 3,
    swamp: 4,
    water: 5,
    cloud: 6,
    hills: 7,
    impassible_mountain: 8
  },
  L1: {
    dwarvern_cave: 1,
    elven_tree: 2,
    halfling_capitol: 3,
    halfling_village: 4,
    human_village: 5,
    lighthouse: 6,
    boats: 7
  },
};

// ----------------------------------------------------------------
// Geometry & Defaults
// ----------------------------------------------------------------
const HEX_WIDTH = 300;
const HEX_HEIGHT = 260;
const X_OFFSET = HEX_WIDTH * 0.75;
const Y_OFFSET = HEX_HEIGHT * 0.5; // used for odd-column offset

const DEFAULT_MIN_ROW = 0;
const DEFAULT_MAX_ROW = 0;
const DEFAULT_MIN_COL = 0;
const DEFAULT_MAX_COL = 0;

const MARGIN = -100; // margin for panning

// ----------------------------------------------------------------
// Helper Functions
// ----------------------------------------------------------------
const isOdd = (n: number): boolean => (((n % 2) + 2) % 2) === 1;

const getHexCenterLocal = (col: number, row: number) => {
  return {
    cx: col * X_OFFSET,
    cy: row * HEX_HEIGHT + (isOdd(col) ? HEX_HEIGHT * 0.5 : 0),
  };
};

const getHexPolygonPoints = (cx: number, cy: number) => {
  const offsetX = HEX_WIDTH / 2;
  const offsetY = HEX_HEIGHT / 2;
  // Adjust the polygon so it fills from 0 to 1 (normalized)
  const points = [
    [HEX_WIDTH * 0.25 - offsetX, 0 - offsetY],
    [HEX_WIDTH * 0.75 - offsetX, 0 - offsetY],
    [HEX_WIDTH - offsetX, HEX_HEIGHT * 0.5 - offsetY],
    [HEX_WIDTH * 0.75 - offsetX, HEX_HEIGHT - offsetY],
    [HEX_WIDTH * 0.25 - offsetX, HEX_HEIGHT - offsetY],
    [0 - offsetX, HEX_HEIGHT * 0.5 - offsetY],
  ];
  return points.map(([x, y]) => `${cx + x},${cy + y}`).join(" ");
};

const computeCanvasSize = (
  minRow: number,
  maxRow: number,
  minCol: number,
  maxCol: number
) => {
  let maxExtent = 0;
  for (let col = minCol; col <= maxCol; col++) {
    for (let row = minRow; row <= maxRow; row++) {
      const { cx, cy } = getHexCenterLocal(col, row);
      const tileLeft = cx - HEX_WIDTH / 2;
      const tileRight = cx + HEX_WIDTH / 2;
      const tileTop = cy - HEX_HEIGHT / 2;
      const tileBottom = cy + HEX_HEIGHT / 2;
      maxExtent = Math.max(
        maxExtent,
        Math.abs(tileLeft),
        Math.abs(tileRight),
        Math.abs(tileTop),
        Math.abs(tileBottom)
      );
    }
  }
  const requiredSize = 2 * Math.max(HEX_WIDTH, HEX_HEIGHT);
  return Math.max(requiredSize, 2 * maxExtent);
};

const getHexTileFromContent = (
  x: number,
  y: number,
  canvasCenter: number,
  getHexCenter: (col: number, row: number) => { cx: number; cy: number }
) => {
  const localX = x - canvasCenter;
  const localY = y - canvasCenter;
  const approxCol = Math.round(localX / X_OFFSET);
  const offsetYForCol = isOdd(approxCol) ? HEX_HEIGHT * 0.5 : 0;
  const approxRow = Math.round((localY - offsetYForCol) / HEX_HEIGHT);
  let bestCol = approxCol;
  let bestRow = approxRow;
  let bestDist = Infinity;
  for (let dc = -1; dc <= 1; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      const colCandidate = approxCol + dc;
      const rowCandidate = approxRow + dr;
      const { cx, cy } = getHexCenter(colCandidate, rowCandidate);
      const dx = cx - x;
      const dy = cy - y;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestCol = colCandidate;
        bestRow = rowCandidate;
      }
    }
  }
  return { col: bestCol, row: bestRow };
};

// ----------------------------------------------------------------
// Map Data & Defaults
// ----------------------------------------------------------------
interface MapData {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
  tilesL0: { [key: string]: number };
  tilesL1: { [key: string]: number };
  tilesL2: { [key: string]: number };
  tilesL3: { [key: string]: number };
}

const createEmptyLayer = (
  minRow: number,
  maxRow: number,
  minCol: number,
  maxCol: number
): { [key: string]: number } => {
  const layer: { [key: string]: number } = {};
  for (let col = minCol; col <= maxCol; col++) {
    for (let row = minRow; row <= maxRow; row++) {
      layer[`${col},${row}`] = 0;
    }
  }
  return layer;
};

const initializeMapData = (
  minRow: number,
  maxRow: number,
  minCol: number,
  maxCol: number
): MapData => ({
  minRow,
  maxRow,
  minCol,
  maxCol,
  tilesL0: createEmptyLayer(minRow, maxRow, minCol, maxCol),
  tilesL1: createEmptyLayer(minRow, maxRow, minCol, maxCol),
  tilesL2: createEmptyLayer(minRow, maxRow, minCol, maxCol),
  tilesL3: createEmptyLayer(minRow, maxRow, minCol, maxCol),
});

// ----------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------
interface TransformState {
  scale: number;
  positionX: number;
  positionY: number;
}

const Canvas: React.FC = () => {
  const theme = useTheme();
  const isMobileDevice = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  // Grid bounds (updatable via Toolbar and DPad)
  const [minRow, setMinRow] = useState<number>(DEFAULT_MIN_ROW);
  const [maxRow, setMaxRow] = useState<number>(DEFAULT_MAX_ROW);
  const [minCol, setMinCol] = useState<number>(DEFAULT_MIN_COL);
  const [maxCol, setMaxCol] = useState<number>(DEFAULT_MAX_COL);

  // Orientation state
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.innerWidth > window.innerHeight
  );

  // Pointer (content coordinates) and corresponding hex tile indices.
  const [pointer, setPointer] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hexTile, setHexTile] = useState<{ col: number; row: number }>({ col: 0, row: 0 });

  // Transform state from the zoom library.
  const [transformState, setTransformState] = useState<TransformState>({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });

  const [showDPad, setShowDPad] = useState<boolean>(!isMobileDevice);
  const [showLocator, setShowLocator] = useState<boolean>(!isMobileDevice);
  const [tileSelected, setTileSelected] = useState<boolean>(false);

  /**
   * Selected tile state:
   * For L0 and L1 the selected value is either a number (if a tile is selected) or "erase" (if the eraser is active).
   * For L3 it remains a boolean.
   */
  const [selectedTile, setSelectedTile] = useState<{
    L0: number | "erase";
    L1: number | "erase";
    L3: boolean;
  }>({ L0: 0, L1: 0, L3: false });

  // Map data state.
  const [mapData, setMapData] = useState<MapData>(
    initializeMapData(DEFAULT_MIN_ROW, DEFAULT_MAX_ROW, DEFAULT_MIN_COL, DEFAULT_MAX_COL)
  );

  // State to control whether dragging/panning is disabled.
  const [dragDisabled, setDragDisabled] = useState<boolean>(false);
  // Track whether we are in “painting” mode (drag and paint).
  const [isPainting, setIsPainting] = useState<boolean>(false);
  // Track the last painted tile to avoid repainting the same cell repeatedly.
  const [lastPaintedTile, setLastPaintedTile] = useState<{ col: number; row: number } | null>(null);

  // Load grid bounds and map data from localStorage on mount.
  useEffect(() => {
    const savedMap = localStorage.getItem("map-active");
    if (savedMap) {
      const parsed: MapData = JSON.parse(savedMap);
      setMinRow(parsed.minRow);
      setMaxRow(parsed.maxRow);
      setMinCol(parsed.minCol);
      setMaxCol(parsed.maxCol);
      setMapData(parsed);
    } else {
      const initialMapData = initializeMapData(
        DEFAULT_MIN_ROW,
        DEFAULT_MAX_ROW,
        DEFAULT_MIN_COL,
        DEFAULT_MAX_COL
      );
      localStorage.setItem("map-active", JSON.stringify(initialMapData));
      setMapData(initialMapData);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Refs for container (viewport) and zoom API.
  const containerRef = useRef<HTMLDivElement>(null);
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);

  // Compute canvas size from grid bounds.
  const { canvasSize } = useMemo(() => {
    const computedSize = computeCanvasSize(minRow, maxRow, minCol, maxCol);
    const vp = Math.max(window.innerWidth, window.innerHeight);
    return { canvasSize: Math.max(vp, computedSize) };
  }, [minRow, maxRow, minCol, maxCol]);

  // Center the drawing using the canvas center.
  const canvasCenter = canvasSize / 2;
  const getHexCenter = (col: number, row: number) => {
    const local = getHexCenterLocal(col, row);
    return { cx: local.cx + canvasCenter, cy: local.cy + canvasCenter };
  };

  // Center the view on initialization.
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  useEffect(() => {
    if (
      !hasInitialized &&
      containerRef.current &&
      transformComponentRef.current &&
      (minRow !== DEFAULT_MIN_ROW ||
        maxRow !== DEFAULT_MAX_ROW ||
        minCol !== DEFAULT_MIN_COL ||
        maxCol !== DEFAULT_MAX_COL)
    ) {
      const cw = containerRef.current.clientWidth;
      const ch = containerRef.current.clientHeight;
      const initialX = cw / 2 - canvasSize / 2;
      const initialY = ch / 2 - canvasSize / 2;
      transformComponentRef.current.setTransform(initialX, initialY, 1, 0);
      setTransformState({ positionX: initialX, positionY: initialY, scale: 1 });
      setHasInitialized(true);
    }
  }, [minRow, maxRow, minCol, maxCol, canvasSize, hasInitialized]);

  // Helper function to merge new bounds with current map data.
  const updateMapDataBounds = (
    oldMap: MapData,
    newMinRow: number,
    newMaxRow: number,
    newMinCol: number,
    newMaxCol: number
  ): MapData => {
    const newTilesL0: { [key: string]: number } = {};
    const newTilesL1: { [key: string]: number } = {};
    const newTilesL2: { [key: string]: number } = {};
    const newTilesL3: { [key: string]: number } = {};

    for (let col = newMinCol; col <= newMaxCol; col++) {
      for (let row = newMinRow; row <= newMaxRow; row++) {
        const key = `${col},${row}`;
        newTilesL0[key] =
          oldMap.tilesL0 && oldMap.tilesL0.hasOwnProperty(key)
            ? oldMap.tilesL0[key]
            : 0;
        newTilesL1[key] =
          oldMap.tilesL1 && oldMap.tilesL1.hasOwnProperty(key)
            ? oldMap.tilesL1[key]
            : 0;
        newTilesL2[key] =
          oldMap.tilesL2 && oldMap.tilesL2.hasOwnProperty(key)
            ? oldMap.tilesL2[key]
            : 0;
        newTilesL3[key] =
          oldMap.tilesL3 && oldMap.tilesL3.hasOwnProperty(key)
            ? oldMap.tilesL3[key]
            : 0;
      }
    }
    return {
      minRow: newMinRow,
      maxRow: newMaxRow,
      minCol: newMinCol,
      maxCol: newMaxCol,
      tilesL0: newTilesL0,
      tilesL1: newTilesL1,
      tilesL2: newTilesL2,
      tilesL3: newTilesL3,
    };
  };

  // (Optional) Update grid bounds via Toolbar.
  const setBoundingBox = (
    newMinR: number,
    newMaxR: number,
    newMinC: number,
    newMaxC: number
  ) => {
    setMinRow(newMinR);
    setMaxRow(newMaxR);
    setMinCol(newMinC);
    setMaxCol(newMaxC);
    const newMapData = updateMapDataBounds(mapData, newMinR, newMaxR, newMinC, newMaxC);
    localStorage.setItem("map-active", JSON.stringify(newMapData));
    setMapData(newMapData);
  };

  // Reset the map to default values.
  const resetMap = () => {
    const defaultData = initializeMapData(
      DEFAULT_MIN_ROW,
      DEFAULT_MAX_ROW,
      DEFAULT_MIN_COL,
      DEFAULT_MAX_COL
    );
    setMinRow(defaultData.minRow);
    setMaxRow(defaultData.maxRow);
    setMinCol(defaultData.minCol);
    setMaxCol(defaultData.maxCol);
    localStorage.setItem("map-active", JSON.stringify(defaultData));
    setMapData(defaultData);
  };

  /**
   * Render white hex outlines.
   */
  const generateWhiteHexes = () => {
    const hexes: JSX.Element[] = [];
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        if (row === 0 && col === 0) continue;
        const { cx, cy } = getHexCenter(col, row);
        hexes.push(
          <polygon
            key={`${row}-${col}`}
            points={getHexPolygonPoints(cx, cy)}
            stroke={theme.palette.text.primary}
            strokeWidth="3"
            fill="none"
          />
        );
      }
    }
    return hexes;
  };

  /**
   * Ensure panning stays within bounds.
   */
  const handlePanningStop = (e: any) => {
    if (!containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    const { scale, positionX, positionY } = e.state;
    let left = Infinity,
      right = -Infinity,
      top = Infinity,
      bottom = -Infinity;
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const { cx, cy } = getHexCenter(col, row);
        left = Math.min(left, cx - HEX_WIDTH / 2);
        right = Math.max(right, cx + HEX_WIDTH / 2);
        top = Math.min(top, cy - HEX_HEIGHT / 2);
        bottom = Math.max(bottom, cy + HEX_HEIGHT / 2);
      }
    }
    const viewWidth = cw / scale;
    const viewHeight = ch / scale;
    let newPositionX = positionX;
    let newPositionY = positionY;
    if (right - left < viewWidth) {
      const gridCenterX = (left + right) / 2;
      newPositionX = cw / 2 - gridCenterX * scale;
    } else {
      const allowedXMin = -right * scale + cw + MARGIN;
      const allowedXMax = -left * scale - MARGIN;
      newPositionX = Math.min(Math.max(positionX, allowedXMin), allowedXMax);
    }
    if (bottom - top < viewHeight) {
      const gridCenterY = (top + bottom) / 2;
      newPositionY = ch / 2 - gridCenterY * scale;
    } else {
      const allowedYMin = -bottom * scale + ch + MARGIN;
      const allowedYMax = -top * scale - MARGIN;
      newPositionY = Math.min(Math.max(positionY, allowedYMin), allowedYMax);
    }
    if (newPositionX !== positionX || newPositionY !== positionY) {
      e.setTransform(newPositionX, newPositionY, scale, 300);
      setTransformState({ positionX: newPositionX, positionY: newPositionY, scale });
    }
  };

  const handlePanning = (e: any) => {
    setTransformState(e.state);
  };

  /**
   * Update the hovered hex tile based on mouse position.
   * Also, if in painting mode, call paintTile().
   */
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    const { scale, positionX, positionY } = transformState;
    const contentX = (clientX - positionX) / scale;
    const contentY = (clientY - positionY) / scale;
    setPointer({ x: contentX, y: contentY });
    const tile = getHexTileFromContent(contentX, contentY, canvasCenter, getHexCenter);
    setHexTile(tile);
    if (isPainting && tileSelected) {
      paintTile(tile);
    }
  };

  /**
   * When the user clicks on the canvas (without dragging), paint the current hex.
   */
  const handleCanvasClick = (e: React.MouseEvent) => {
    if (!tileSelected) return;
    paintTile(hexTile);
  };

  /**
   * Helper function to paint a tile.
   * Only updates the layer corresponding to the active tool.
   */
  const paintTile = (tile: { col: number; row: number }) => {
    if (tile.col < minCol || tile.col > maxCol || tile.row < minRow || tile.row > maxRow)
      return;
    if (
      lastPaintedTile &&
      lastPaintedTile.col === tile.col &&
      lastPaintedTile.row === tile.row
    )
      return;
    setLastPaintedTile(tile);
    const key = `${tile.col},${tile.row}`;
    const updatedMap = { ...mapData };

    // Update L0 layer only if its tool is active (non-zero)
    if (selectedTile.L0 !== 0) {
      if (selectedTile.L0 === "erase") {
        updatedMap.tilesL0[key] = 0;
      } else if (typeof selectedTile.L0 === "number" && selectedTile.L0 > 0) {
        updatedMap.tilesL0[key] = selectedTile.L0;
      }
    }
    // Update L1 layer only if its tool is active.
    if (selectedTile.L1 !== 0) {
      if (selectedTile.L1 === "erase") {
        updatedMap.tilesL1[key] = 0;
      } else if (typeof selectedTile.L1 === "number" && selectedTile.L1 > 0) {
        updatedMap.tilesL1[key] = selectedTile.L1;
      }
    }
    // Update L3 if active.
    if (selectedTile.L3 === true) {
      updatedMap.tilesL3[key] = 1;
    }
    setMapData(updatedMap);
    localStorage.setItem("map-active", JSON.stringify(updatedMap));
  };

  /**
   * On mouse down over the SVG:
   * - If a texture is selected, begin painting and paint the hex underneath.
   * - Otherwise, if over a grid cell, disable dragging.
   */
  const handleSvgMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    const { scale, positionX, positionY } = transformState;
    const contentX = (clientX - positionX) / scale;
    const contentY = (clientY - positionY) / scale;
    const tile = getHexTileFromContent(contentX, contentY, canvasCenter, getHexCenter);
    if (tileSelected) {
      setIsPainting(true);
      setDragDisabled(true);
      paintTile(tile);
    } else {
      if (tile.col >= minCol && tile.col <= maxCol && tile.row >= minRow && tile.row <= maxRow) {
        setDragDisabled(true);
      } else {
        setDragDisabled(false);
      }
    }
  };

  /**
   * On mouse up, end painting (if active) and re-enable dragging.
   */
  const handleSvgMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    setDragDisabled(false);
    if (tileSelected) {
      setIsPainting(false);
      setLastPaintedTile(null);
    }
  };

  /**
   * Render the placed tiles.
   */
  const renderMapTiles = () => {
    const elements = [];
    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const key = `${col},${row}`;
        const { cx, cy } = getHexCenter(col, row);
        const x = cx - HEX_WIDTH / 2;
        const y = cy - HEX_HEIGHT / 2;

        if (mapData.tilesL0[key] > 0) {
          let tileName = "";
          for (const [name, num] of Object.entries(tileGroups.L0)) {
            if (num === mapData.tilesL0[key]) {
              tileName = name;
              break;
            }
          }
          if (tileName) {
            elements.push(
              <image
                key={`L0-${key}`}
                href={`/tile_types/${tileName}.png`}
                x={x}
                y={y}
                width={HEX_WIDTH}
                height={HEX_HEIGHT}
                clipPath="url(#hexClip)"
                preserveAspectRatio="xMidYMid slice"
              />
            );
          }
        }
        if (mapData.tilesL1[key] > 0) {
          let tileName = "";
          for (const [name, num] of Object.entries(tileGroups.L1)) {
            if (num === mapData.tilesL1[key]) {
              tileName = name;
              break;
            }
          }
          if (tileName) {
            elements.push(
              <image
                key={`L1-${key}`}
                href={`/tile_types/${tileName}.png`}
                x={x}
                y={y}
                width={HEX_WIDTH}
                height={HEX_HEIGHT}
                clipPath="url(#hexClip)"
                preserveAspectRatio="xMidYMid slice"
              />
            );
          }
        }
        if (mapData.tilesL3[key] > 0) {
          elements.push(
            <polygon
              key={`L3-${key}`}
              points={getHexPolygonPoints(cx, cy)}
              fill={theme.palette.primary.main}
              opacity={0.5}
            />
          );
        }
      }
    }
    return elements;
  };

  // Compute the cursor style:
  // - If a texture is selected, always use "crosshair".
  // - Otherwise, if the pointer is over a grid cell, use "default".
  // - Else (outside the grid) show "grab".
  const computedCursor =
    tileSelected
      ? "crosshair"
      : (hexTile.col >= minCol && hexTile.col <= maxCol && hexTile.row >= minRow && hexTile.row <= maxRow)
        ? "default"
        : "grab";

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Toolbar Sidebar */}
      <Toolbar
        boundingBox={{ minRow, maxRow, minCol, maxCol }}
        setBoundingBox={setBoundingBox}
        resetMap={resetMap}
        showDPad={showDPad}
        setShowDPad={setShowDPad}
        showLocator={showLocator}
        setShowLocator={setShowLocator}
        onTileSelect={(selection) => {
          if (selection.L0 === "erase") {
            // L0 erase: set only L0 to "erase" and clear L1.
            setSelectedTile({ L0: "erase", L1: 0, L3: false });
            setTileSelected(true);
          } else if (selection.L1 === "erase") {
            // L1 erase: set only L1 to "erase" and clear L0.
            setSelectedTile({ L0: 0, L1: "erase", L3: false });
            setTileSelected(true);
          } else if (selection.L0) {
            const tileNumber = tileGroups.L0[selection.L0] || 0;
            setSelectedTile({ L0: tileNumber, L1: 0, L3: false });
            setTileSelected(true);
          } else if (selection.L1) {
            const tileNumber = tileGroups.L1[selection.L1] || 0;
            setSelectedTile({ L0: 0, L1: tileNumber, L3: false });
            setTileSelected(true);
          } else if (selection.L3) {
            setSelectedTile({ L0: 0, L1: 0, L3: true });
            setTileSelected(true);
          } else {
            setSelectedTile({ L0: 0, L1: 0, L3: false });
            setTileSelected(false);
          }
        }}
      />

      {/* The zoomable SVG canvas */}
      <Box
        ref={containerRef}
        className="canvas-bg"
        sx={{
          flexGrow: 1,
          position: "relative",
          overflow: "hidden",
          cursor: computedCursor,
        }}
        onMouseMove={handleMouseMove}
      >
        <TransformWrapper
          ref={transformComponentRef}
          limitToBounds={false}
          minScale={0.05}
          maxScale={5}
          onPanning={handlePanning}
          onPanningStop={handlePanningStop}
          onZoom={(e: any) => setTransformState(e.state)}
          panning={{ disabled: dragDisabled }}
        >
          <TransformComponent>
            <svg
              id="export-svg"
              width={canvasSize}
              height={canvasSize}
              viewBox={`0 0 ${canvasSize} ${canvasSize}`}
              onClick={handleCanvasClick}
              onMouseDown={handleSvgMouseDown}
              onMouseUp={handleSvgMouseUp}
            >
              <defs>
                <clipPath id="hexClip" clipPathUnits="objectBoundingBox">
                  <polygon
                    points="
                      0.25 0,
                      0.75 0,
                      1 0.5,
                      0.75 1,
                      0.25 1,
                      0 0.5
                    "
                  />
                </clipPath>
              </defs>
              {renderMapTiles()}
              {generateWhiteHexes()}
              {(() => {
                const { cx, cy } = getHexCenter(0, 0);
                return (
                  <polygon
                    key="red-hex"
                    points={getHexPolygonPoints(cx, cy)}
                    stroke={theme.palette.primary.main}
                    strokeWidth="3"
                    fill="none"
                  />
                );
              })()}
              {tileSelected &&
                hexTile.col >= minCol &&
                hexTile.col <= maxCol &&
                hexTile.row >= minRow &&
                hexTile.row <= maxRow && (
                  (() => {
                    const { cx, cy } = getHexCenter(hexTile.col, hexTile.row);
                    const points = getHexPolygonPoints(cx, cy);
                    return (
                      <polygon
                        key="hover-overlay"
                        points={points}
                        fill="rgba(255,87,84,0.5)"
                        style={{ backdropFilter: "blur(2px)" }}
                      />
                    );
                  })()
                )}
            </svg>
          </TransformComponent>
        </TransformWrapper>

        {showLocator && (
          <Locator
            pointer={pointer}
            hexTile={hexTile}
            boundingBox={{ minRow, maxRow, minCol, maxCol }}
          />
        )}
      </Box>

      {showDPad && (
        <DPad
          boundingBox={{ minRow, maxRow, minCol, maxCol }}
          setBoundingBox={setBoundingBox}
        />
      )}

      <Dialog
        open={!isLandscape}
        PaperProps={{ sx: { backgroundColor: "#444", color: "white" } }}
      >
        <DialogTitle>Rotate Device</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Please rotate your device to <strong>landscape</strong> mode.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Canvas;